import { createTwilioIntegrationThunk } from '../../../redux/thunks/integration';
import { createTwilioSchema } from '../../../schema/integrationSchema';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: DeletePipelineModal
 * @paragraph: A modal component for confirming the deletion of a pipeline.
 * Displays a warning message and provides options to cancel or confirm the deletion.
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle the modal close action.
 */

interface CreateTwilioInterface {
    account_sid: string;
    api_sid: string;
    auth_token: string;
    phone_number: string;
    client_id: number;
}

const CreateTwilioIntegration = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders/selectedPipeline: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { loaders } = useSelector((state: RootState) => state.integration);
    const initialValues: CreateTwilioInterface = {
        account_sid: '',
        api_sid: '',
        auth_token: '',
        phone_number: '',
        client_id: id && (currentUser?.role === 'client') ? 0 : id ? parseInt(id as string, 10) : 0
    };

    /**
     * @heading3: Functions
     * @heaidng4: Handles the deletion of the pipeline.
     * @paragraph: Dispatches the createTwilioIntegrationThunk to perform the deletion.
     */
    const handleSubmit = async (values: CreateTwilioInterface) => {
        try {
            await dispatch(createTwilioIntegrationThunk({
                ...values,
                paramId: id
            })).unwrap();
            // Close the modal after successful deletion
            onClose();
        } catch (e) {
            // Catch Errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Connect Twilio">
        <Formik
            initialValues={initialValues}
            validationSchema={createTwilioSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2} alignItems="start">
                        <CustomInput
                            value={values.account_sid}
                            onChange={handleChange('account_sid')}
                            onBlur={handleBlur('account_sid')}
                            error={touched.account_sid && errors.account_sid}
                            fullWidth
                            label="Account SID"
                            placeholder="Account SID"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <CustomInput
                            value={values.api_sid}
                            onChange={handleChange('api_sid')}
                            onBlur={handleBlur('api_sid')}
                            error={touched.api_sid && errors.api_sid}
                            fullWidth
                            label="API SID"
                            placeholder="API SID"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <CustomInput
                            value={values.auth_token}
                            onChange={handleChange('auth_token')}
                            onBlur={handleBlur('auth_token')}
                            error={touched.auth_token && errors.auth_token}
                            fullWidth
                            label="API Secret Key"
                            placeholder="API Secret Key"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <CustomInput
                            type="phone"
                            value={values.phone_number}
                            onChangeForPhone={handleChange('phone_number')}
                            onBlur={handleBlur('phone_number')}
                            error={touched.phone_number && errors.phone_number}
                            fullWidth
                            label="Phone Number"
                            placeholder="201-555-0123"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <Button variant="text"
                            href="https://help.gonzy.com/en/article/connecting-twilio-rv5a48/"
                            target="_blank"
                            rel="noopener noreferrer">How to connect your Twilio account?</Button>
                        <Box display="flex" gap={2} width="100%">
                            <Button variant="outlined" color="secondary" onClick={onClose} fullWidth disabled={loaders.isCreating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" fullWidth disabled={loaders.isCreating}>
                                {loaders.isCreating ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default CreateTwilioIntegration;